import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Phone, MessageCircle, X, Sparkles } from 'lucide-react';

// Flashing site-wide advert for Tax & Loan services. Sticky at top, dismissible per session.
const TaxAdBanner = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ whatsapp_number: '', support_phone: '', whatsapp_message: '' });
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('tax_ad_dismissed') === '1');
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    axios.get(`${API}/config/public`).then((r) => setConfig(r.data || {})).catch(() => {});
  }, []);

  // Rotate the highlighted service every 3s
  useEffect(() => {
    if (dismissed) return undefined;
    const t = setInterval(() => setHighlight((h) => (h + 1) % services.length), 3000);
    return () => clearInterval(t);
  }, [dismissed]);

  if (dismissed) return null;

  const services = [
    { label: 'ITR Filing', emoji: '📊', from: '₹499' },
    { label: 'GST Returns', emoji: '🧾', from: '₹999' },
    { label: 'Home Loans', emoji: '🏠', from: '8.4%' },
    { label: 'Personal Loans', emoji: '💼', from: '10.5%' },
    { label: 'Credit Cards', emoji: '💳', from: 'Compare' },
    { label: 'Tax Planning', emoji: '✨', from: '₹1,999' },
  ];

  const phoneDigits = (config.support_phone || '+917054986771').replace(/[^\d]/g, '');
  const waPhone = (config.whatsapp_number || '917054986771').replace(/[^\d]/g, '');
  const waMsg = encodeURIComponent(config.whatsapp_message || 'Hi Arthik team, I would like help with tax filing / loan / financial services.');
  const tel = `tel:+${phoneDigits}`;
  const wa = `https://wa.me/${waPhone}?text=${waMsg}`;

  const active = services[highlight];

  return (
    <div data-testid="tax-ad-banner" className="tax-ad-banner-wrap">
      <div className="tax-ad-banner">
        <div className="tax-ad-shine" aria-hidden="true" />
        <Sparkles size={14} className="tax-ad-sparkle" />

        <div className="tax-ad-text">
          <span className="tax-ad-pulse-dot" />
          <strong style={{ color: '#FFF' }}>Need help with</strong>
          <span className="tax-ad-rotator" key={highlight}>
            <span style={{ fontSize: 16 }}>{active.emoji}</span> {active.label} <em style={{ color: '#FEF08A', fontStyle: 'normal', fontWeight: 700 }}>{active.from}</em>
          </span>
          <span style={{ opacity: 0.85 }}>?</span>
          <span className="tax-ad-divider" />
          <a href={tel} className="tax-ad-action tax-ad-call" data-testid="tax-ad-call-btn" aria-label={`Call ${phoneDigits}`}>
            <Phone size={14} /> <span>Call</span>
            <span className="tax-ad-num">{config.support_phone || '+91 7054986771'}</span>
          </a>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="tax-ad-action tax-ad-wa" data-testid="tax-ad-wa-btn" aria-label="WhatsApp us">
            <MessageCircle size={14} /> <span>WhatsApp</span>
          </a>
          <button
            onClick={() => navigate('/tax-services')}
            className="tax-ad-action tax-ad-explore"
            data-testid="tax-ad-explore-btn"
            type="button"
          >
            Explore Tax Services
          </button>
        </div>

        <button
          onClick={() => { setDismissed(true); sessionStorage.setItem('tax_ad_dismissed', '1'); }}
          aria-label="Dismiss"
          className="tax-ad-close"
          data-testid="tax-ad-close-btn"
        >
          <X size={14} />
        </button>
      </div>

      <style>{`
        .tax-ad-banner-wrap {
          width: 100%;
          background: transparent;
          position: relative;
          z-index: 50;
        }
        .tax-ad-banner {
          position: relative;
          overflow: hidden;
          background: linear-gradient(90deg, #FF6B35 0%, #F97316 30%, #10B981 70%, #059669 100%);
          background-size: 200% 100%;
          animation: tax-ad-bg-shift 6s linear infinite;
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 12px 8px 18px;
          font-size: 0.9rem;
          line-height: 1.2;
        }
        .tax-ad-shine {
          position: absolute; top: 0; left: -150%; width: 80%; height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
          animation: tax-ad-shine 3.4s linear infinite;
          pointer-events: none;
        }
        .tax-ad-sparkle { color: #FEF08A; animation: tax-ad-sparkle-rot 2.4s linear infinite; flex-shrink: 0; }
        .tax-ad-text {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1; position: relative; z-index: 1;
        }
        .tax-ad-pulse-dot {
          display: inline-block; width: 9px; height: 9px; border-radius: 50%;
          background: #FEF08A; box-shadow: 0 0 0 0 rgba(254, 240, 138, 0.85);
          animation: tax-ad-pulse 1.4s ease-out infinite;
        }
        .tax-ad-rotator {
          background: rgba(255,255,255,0.18); padding: 3px 10px; border-radius: 999px;
          font-weight: 700; display: inline-flex; align-items: center; gap: 6px;
          backdrop-filter: blur(4px);
          animation: tax-ad-fade 0.45s ease-out;
        }
        .tax-ad-divider { width: 1px; background: rgba(255,255,255,0.4); height: 18px; }
        .tax-ad-action {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 999px;
          font-weight: 700; font-size: 0.8rem; text-decoration: none;
          border: 1px solid rgba(255,255,255,0.55); background: rgba(255,255,255,0.12);
          color: white; cursor: pointer; transition: transform 0.15s ease, background 0.15s ease;
          white-space: nowrap;
        }
        .tax-ad-action:hover { transform: translateY(-1px); background: rgba(255,255,255,0.22); }
        .tax-ad-call { animation: tax-ad-shake 4s ease-in-out infinite; }
        .tax-ad-wa { background: #25D366; border-color: #25D366; }
        .tax-ad-wa:hover { background: #1ebd5b; }
        .tax-ad-explore { background: white; color: #FF6B35; border-color: white; }
        .tax-ad-num { font-weight: 800; opacity: 0.95; }
        .tax-ad-close {
          background: transparent; border: 1px solid rgba(255,255,255,0.5); color: white;
          width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; flex-shrink: 0; position: relative; z-index: 1;
        }
        .tax-ad-close:hover { background: rgba(255,255,255,0.2); }

        @keyframes tax-ad-bg-shift { 0% { background-position: 0% 0; } 100% { background-position: 200% 0; } }
        @keyframes tax-ad-shine { 0% { left: -150%; } 100% { left: 150%; } }
        @keyframes tax-ad-pulse {
          0% { box-shadow: 0 0 0 0 rgba(254, 240, 138, 0.85); }
          70% { box-shadow: 0 0 0 10px rgba(254, 240, 138, 0); }
          100% { box-shadow: 0 0 0 0 rgba(254, 240, 138, 0); }
        }
        @keyframes tax-ad-sparkle-rot {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.15); }
          100% { transform: rotate(360deg) scale(1); }
        }
        @keyframes tax-ad-shake {
          0%, 90%, 100% { transform: rotate(0deg); }
          92% { transform: rotate(-8deg); }
          94% { transform: rotate(8deg); }
          96% { transform: rotate(-6deg); }
          98% { transform: rotate(4deg); }
        }
        @keyframes tax-ad-fade {
          0% { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 720px) {
          .tax-ad-banner { padding: 7px 10px; font-size: 0.78rem; }
          .tax-ad-num { display: none; }
          .tax-ad-text > strong { display: none; }
          .tax-ad-divider { display: none; }
        }
      `}</style>
    </div>
  );
};

export default TaxAdBanner;
